import { useEffect } from 'react'

const DEFAULT_TITLE = 'Lilla improteatern'
const CANONICAL_ORIGIN = 'https://improteatern.se'

interface SEOData {
  title: string;
  description: string;
  canonicalPath?: string;
  structuredData?: object;
  ogImage?: string;
}

export function usePageTitle(title: string) {
  useEffect(() => {
    document.title = title
    
    // Cleanup: restore default title when component unmounts
    return () => {
      document.title = DEFAULT_TITLE
    }
  }, [title])
}

export function usePageSEO(seoData: SEOData) {
  useEffect(() => {
    // Set page title
    document.title = seoData.title;
    
    // Set meta description
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', seoData.description);

    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    const canonicalUrl = seoData.canonicalPath
      ? CANONICAL_ORIGIN + seoData.canonicalPath
      : CANONICAL_ORIGIN + window.location.pathname;
    canonical.setAttribute('href', canonicalUrl);

    // Set og:image / twitter:image (per-page override)
    const setMetaProp = (selector: string, attr: 'name' | 'property', key: string, value: string) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', value);
    };
    if (seoData.ogImage) {
      setMetaProp('meta[property="og:image"]', 'property', 'og:image', seoData.ogImage);
      setMetaProp('meta[name="twitter:image"]', 'name', 'twitter:image', seoData.ogImage);
    }
    setMetaProp('meta[property="og:title"]', 'property', 'og:title', seoData.title);
    setMetaProp('meta[property="og:description"]', 'property', 'og:description', seoData.description);
    setMetaProp('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    setMetaProp('meta[name="twitter:title"]', 'name', 'twitter:title', seoData.title);
    setMetaProp('meta[name="twitter:description"]', 'name', 'twitter:description', seoData.description);

    // Set structured data
    if (seoData.structuredData) {
      const scriptId = 'ld-json-page';
      let script = document.getElementById(scriptId) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = scriptId;
        document.head.appendChild(script);
      }
      script.text = JSON.stringify(seoData.structuredData);
    }

    // Cleanup
    return () => {
      document.title = DEFAULT_TITLE;
    }
  }, [seoData.title, seoData.description, seoData.canonicalPath, seoData.structuredData, seoData.ogImage]);
}